import React, { Component } from 'react'
import dateFns from 'date-fns'
import PropTypes from 'prop-types'
import { FormattedMessage } from 'react-intl'
import { TooltipOnHover } from 'components/Utils/TooltipOnHover'

class Timeago extends Component {
  static propTypes = {
    date: PropTypes.string.isRequired,
    lowercase: PropTypes.bool,
    formatToday: PropTypes.func,
    shortFormat: PropTypes.bool,
  }

  constructor(props) {
    super(props)
    this.state = this.computeDistance(props.date)
  }

  shouldComponentUpdate(nextProps, nextState) {
    return (
      nextProps.date !== this.props.date ||
      this.state.frame !== nextState.frame ||
      this.state.count !== nextState.count
    )
  }

  componentDidMount() {
    this.tick()
  }

  componentWillReceiveProps(props) {
    this.setState(this.computeDistance(props.date))
  }

  componentWillUnmount() {
    clearTimeout(this.timeout)
  }

  tick = () => {
    this.setState(this.computeDistance())
    clearTimeout(this.timeout)
    if (this.state.refresh) {
      this.timeout = setTimeout(this.tick, this.state.refresh * 1000)
    }
  }

  computeDistance = (date = this.props.date) => {
    const now = Date.now()
    const from = new Date(date).getTime()
    const diff = Math.floor((now - from) / 1000)
    if (diff < 60) {
      return {
        frame: 'second',
        count: diff,
        refresh: 14,
      }
    } else if (diff < 3600) {
      // 1 hour
      return {
        frame: 'minute',
        count: Math.floor(diff / 60),
        refresh: 55,
      }
    } else if (
      // 4 hours
      diff < 14400 ||
      // 24h
      (diff < 86400 && this.props.shortFormat)
    ) {
      return {
        frame: 'hour',
        count: Math.floor(diff / 3600),
        refresh: 3400,
      }
    } else if (dateFns.isToday(date)) {
      return {
        frame: 'today',
        count: Math.floor(diff / 3600),
        refresh: 3400,
      }
    } else if (dateFns.isYesterday(date) && !this.props.shortFormat) {
      return {
        frame: 'yesterday',
        count: Math.floor(diff / 3600),
        refresh: 3400,
      }
    } else if (diff < 604800) {
      // A week
      return {
        frame: 'day',
        count: Math.floor(diff / 86400),
        refresh: null,
      }
    } else if (diff < 2592000) {
      // A month
      return {
        frame: 'week',
        count: Math.floor(diff / 604800),
        refresh: null,
      }
    } else if (diff < 31536000) {
      // A year
      return {
        frame: 'month',
        count: Math.floor(diff / 2592000),
        refresh: null,
      }
    }
    return {
      frame: 'date',
      count: Math.floor(diff / 3600),
      refresh: null,
    }
  }

  getFormattedDate(frame = this.state.frame) {
    const { date, lowercase, formatToday, shortFormat } = this.props
    switch (frame) {
      case 'second':
        return shortFormat ? 'now' : 'few moments ago'
      case 'minute':
        return (
          <FormattedMessage
            id="time.minutes"
            defaultMessage={`{count, plural,
            one {1${shortFormat ? 'm' : 'min ago'}}
            other {#${shortFormat ? 'm' : 'min ago'}}
          }`}
            values={{ count: this.state.count }}
          />
        )
      case 'hour':
        return (
          <FormattedMessage
            id="time.hours"
            defaultMessage={`{count, plural,
            one {1${shortFormat ? 'h' : 'h ago'}}
            other {#${shortFormat ? 'h' : 'h ago'}}
          }`}
            values={{ count: this.state.count }}
          />
        )
      case 'today':
        if (formatToday) {
          return formatToday(date)
        }
        if (lowercase) {
          return 'today'
        }
        return 'Today'
      case 'yesterday':
        return lowercase ? `yesterday` : `Yesterday`
      case 'day':
        return (
          <FormattedMessage
            id="time.days"
            defaultMessage={`{count, plural,
            one {1${shortFormat ? 'd' : ' day ago'}}
            other {#${shortFormat ? 'd' : ' days ago'}}
          }`}
            values={{ count: this.state.count }}
          />
        )
      case 'week':
        return (
          <FormattedMessage
            id="time.weeks"
            defaultMessage={`{count, plural,
            one {1${shortFormat ? 'w' : ' week ago'}}
            other {#${shortFormat ? 'w' : ' weeks ago'}}
          }`}
            values={{ count: this.state.count }}
          />
        )
      case 'month':
        return (
          <FormattedMessage
            id="time.months"
            defaultMessage={`{count, plural,
            one {1${shortFormat ? 'mo' : ' month ago'}}
            other {#${shortFormat ? 'mo' : ' months ago'}}
          }`}
            values={{ count: this.state.count }}
          />
        )
      default:
        return dateFns.format(date, 'DD/MM/YYYY')
    }
  }

  render() {
    const dateElement = <span>{this.getFormattedDate()}</span>
    if (this.state.frame === 'date') {
      return dateElement
    }
    return (
      <TooltipOnHover
        id="timeago"
        delayShow={500}
        placement="top"
        tooltipContent={this.getFormattedDate('date')}
      >
        {dateElement}
      </TooltipOnHover>
    )
  }
}

export default Timeago
